import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { dashboardApiClient } from '../../../services/api'
import {
  Button,
  FormSection,
  LoadingSpinner,
} from '@airclou/ui'
import {
  Monitor,
  Smartphone,
  Tablet,
  MapPin,
  Clock,
  LogOut,
} from 'lucide-react'
import { ErrorState } from '../../../components/state/ErrorState'
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateTimeInTz } from '@airclou/utils'

interface UserSession {
  id: string;
  is_current: boolean;
  /** Absent from an auth service older than the flag — read that as active. */
  is_active?: boolean;
  created_at: string;
  last_used: string;
  expires_at: string;
  ip_address: string | null;
  user_agent: string | null;
  device_info: Record<string, any>;
  browser: string;
  os: string;
  device_type: string;
}

export function ActiveSessionsTab() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard')
  const [sessions, setSessions] = useState<UserSession[]>([])
  const [isLoadingSessions, setIsLoadingSessions] = useState(true)
  const [sessionsError, setSessionsError] = useState<string | null>(null)
  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null)
  const [isRevokingAll, setIsRevokingAll] = useState(false)

  // Load sessions on mount
  useEffect(() => {
    loadSessions()
  }, [])

  const loadSessions = async () => {
    setIsLoadingSessions(true)
    setSessionsError(null)
    try {
      const response = await dashboardApiClient.getUserSessions()
      setSessions(response)
    } catch (error) {
      console.error('Failed to load sessions:', error)
      setSessionsError(error instanceof Error ? error.message : t('security.failedToLoadSessions'))
    } finally {
      setIsLoadingSessions(false)
    }
  }

  const handleRevokeSession = async (sessionId: string) => {
    setRevokingSessionId(sessionId)
    try {
      await dashboardApiClient.revokeSession(sessionId)
      // Remove the revoked session from the list
      setSessions(prev => prev.filter(s => s.id !== sessionId))
    } catch (error) {
      console.error('Failed to revoke session:', error)
      setSessionsError(error instanceof Error ? error.message : t('security.failedToRevokeSession'))
    } finally {
      setRevokingSessionId(null)
    }
  }

  const handleRevokeAllOtherSessions = async () => {
    if (!confirm(t('security.signOutConfirm'))) {
      return
    }

    setIsRevokingAll(true)
    try {
      const response = await dashboardApiClient.revokeAllOtherSessions()
      // Reload sessions to show only current one
      await loadSessions()
      alert(t('security.signedOutCount', { count: response.count }))
    } catch (error) {
      console.error('Failed to revoke sessions:', error)
      setSessionsError(error instanceof Error ? error.message : t('security.failedToRevokeSessions'))
    } finally {
      setIsRevokingAll(false)
    }
  }

  return (
    <FormSection
      title={t('security.activeSessions')}
      actions={
        sessions.length > 1 && !isLoadingSessions && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleRevokeAllOtherSessions}
            disabled={isRevokingAll}
          >
            <LogOut className="h-4 w-4 mr-2" />
            {t('security.signOutAllOther')}
          </Button>
        )
      }
    >
      {isLoadingSessions ? (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : sessionsError ? (
        <ErrorState
          title={t('security.failedToLoadSessions')}
          description={sessionsError}
          onRetry={loadSessions}
        />
      ) : sessions.length === 0 ? (
        <p className="text-muted-foreground py-4">{t('security.noActiveSessions')}</p>
      ) : (
        <div className="space-y-4">
          {sessions.map((session) => {
            const DeviceIcon = session.device_type === 'Mobile' ? Smartphone : 
                              session.device_type === 'Tablet' ? Tablet : Monitor
            
            return (
              <div 
                key={session.id} 
                className={`p-4 border rounded-lg ${session.is_current ? 'border-primary/40 bg-primary/5' : 'border-border'}`}
              >
                <div className="flex items-start justify-between">
                  <div className={`flex gap-3 ${session.is_active === false ? 'opacity-60' : ''}`}>
                    <div className="mt-1">
                      <DeviceIcon className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {/* h4, not h3: these are rows inside the section
                            whose own heading is the h3 above them. */}
                        <h4 className="font-medium text-foreground">
                          {session.browser} on {session.os}
                        </h4>
                        {session.is_current && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-primary/15 text-primary rounded-full">
                            {t('security.currentSession')}
                          </span>
                        )}
                        {session.is_active === false && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground rounded-full">
                            {t('security.inactiveSession')}
                          </span>
                        )}
                      </div>
                      
                      <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                        {session.is_active === false && (
                          <p>{t('security.inactiveSessionHint')}</p>
                        )}
                        {session.ip_address && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5" />
                            <span>IP: {session.ip_address}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          <span>
                            {t('security.lastActive', { date: formatDateTimeInTz(session.last_used, timezone) })}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          <span>
                            {t('security.sessionCreated', { date: formatDateTimeInTz(session.created_at, timezone) })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {!session.is_current && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRevokeSession(session.id)}
                      disabled={revokingSessionId === session.id}
                    >
                      {revokingSessionId === session.id ? (
                        <>
                          <LoadingSpinner size="small" />
                          <span className="ml-2">{t('security.signingOut')}</span>
                        </>
                      ) : (
                        <>
                          <LogOut className="h-4 w-4 mr-2" />
                          {t('security.signOut')}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </FormSection>
  )
}
